import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

// Request interceptor to add auth header
axios.interceptors.request.use(
  (config) => {
    // Check if this is an admin route
    if (config.url.includes('/admin/') && !config.url.includes('/admin/login')) {
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        config.headers.Authorization = `Bearer ${adminToken}`;
      }
    } else {
      // Regular user token
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/admin/login') || url.includes('/auth/change-password') || url.includes('/users/me');
    if ((status === 401 || status === 403) && !isAuthCall && !url.includes('/admin/')) {
      const detail = error.response?.data?.detail;
      if (status === 401 || (typeof detail === 'string' && detail.toLowerCase().includes('account'))) {
        localStorage.removeItem('token');
        if (detail) sessionStorage.setItem('auth_notice', typeof detail === 'string' ? detail : 'Your session has ended. Please sign in again.');
        window.location.href = '/';
      }
    }
    if (status === 401 && url.includes('/admin/') && !url.includes('/admin/login')) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminInfo');
      window.location.href = '/admin';
    }
    return Promise.reject(error);
  }
);

const apiService = {
  // Authentication
  auth: {
    login: (email, password) => axios.post('/auth/login', { email, password }),
    register: (userData) => axios.post('/auth/register', userData),
    logout: () => axios.post('/auth/logout'),
    verify: () => axios.get('/auth/verify')
  },

  // User management
  users: {
    getMe: () => axios.get('/users/me'),
    updateMe: (userData) => axios.put('/users/me', userData),
    getDiscovery: () => axios.get('/users/discovery'),
    uploadPhotos: (files) => {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      return axios.post('/users/upload-photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    deletePhoto: (photoId) => axios.delete(`/users/photos/${photoId}`),
    getSettings: () => axios.get('/users/settings'),
    updateSettings: (settings) => axios.put('/users/settings', settings)
  },

  // Swipes and matches
  swipes: {
    swipe: (targetUserId, action) => axios.post('/swipes/', { 
      target_user_id: targetUserId, 
      action 
    }),
    swipeWithData: (swipeData) => axios.post('/swipes/', swipeData),
    getMatches: () => axios.get('/swipes/matches'),
    unmatch: (matchId) => axios.delete(`/swipes/matches/${matchId}`),
    getTipOffers: () => axios.get('/swipes/tip-offers'),
    likeAllowance: () => axios.get('/swipes/like-allowance'),
    likesReceivedCount: () => axios.get('/swipes/likes-received/count'),
    likesReceived: () => axios.get('/swipes/likes-received')
  },

  // Messages
  messages: {
    getMessages: (matchId) => axios.get(`/messages/matches/${matchId}/messages`),
    sendMessage: (matchId, message, messageType = 'text') => 
      axios.post(`/messages/matches/${matchId}/messages`, {
        message,
        message_type: messageType
      }),
    markAsRead: (messageId) => axios.put(`/messages/${messageId}/read`)
  },

  // Notifications
  notifications: {
    getNotifications: (params = {}) => axios.get('/notifications/', { params }),
    markAsRead: (notificationId) => axios.patch(`/notifications/${notificationId}/read`),
    markAllAsRead: () => axios.patch('/notifications/read-all'),
    getUnreadCount: () => axios.get('/notifications/unread-count'),
    getSettings: () => axios.get('/notifications/settings'),
    updateSettings: (settings) => axios.patch('/notifications/settings', settings),
    deleteNotification: (notificationId) => axios.delete(`/notifications/${notificationId}`),
    deleteAllNotifications: () => axios.delete('/notifications/')
  },

  // Tips
  tips: {
    respondToTip: (responseData) => axios.post('/tips/respond', responseData),
    getReceivedTips: () => axios.get('/tips/received'),
    getSentTips: () => axios.get('/tips/sent')
  },

  // Videos
  videos: {
    upload: (formData) => axios.post('/videos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getMyVideos: () => axios.get('/videos/my-videos'),
    getVideo: (videoId) => axios.get(`/videos/${videoId}`),
    deleteVideo: (videoId) => axios.delete(`/videos/${videoId}`),
    getVideoStatus: (videoId) => axios.get(`/videos/status/${videoId}`),
    streamVideo: (videoId, quality = 'sd') => `${API}/videos/stream/${videoId}/${quality}`,
    getThumbnail: (videoId) => `${API}/videos/thumbnail/${videoId}`
  },

  // AI Matching
  aiMatching: {
    analyzePersonality: () => axios.post('/ai-matching/analyze-personality'),
    getPersonality: () => axios.get('/ai-matching/personality'),
    calculateCompatibility: (targetUserId) => axios.post(`/ai-matching/compatibility/${targetUserId}`),
    getConversationStarters: (targetUserId) => axios.get(`/ai-matching/conversation-starters/${targetUserId}`),
    getEnhancedMatches: (params = { limit: 20 }) => axios.get('/ai-matching/enhanced-matches', { params }),
    getMatchInsights: () => axios.get('/ai-matching/match-insights'),
    refreshPersonality: () => axios.post('/ai-matching/refresh-personality')
  },

  // Admin endpoints
  admin: {
    login: (username, password) => axios.post('/admin/login', { username, password }),
    getUsers: (params = {}) => axios.get('/admin/users', { params }),
    getUserDetails: (userId) => axios.get(`/admin/users/${userId}`),
    updateUserStatus: (userId, statusData) => axios.patch(`/admin/users/${userId}/status`, statusData),
    updateUserPrivileges: (userId, privilegeData) => axios.patch(`/admin/users/${userId}/privileges`, privilegeData),
    updateUserProfile: (userId, profileData) => axios.put(`/admin/users/${userId}`, profileData),
    deleteUser: (userId) => axios.delete(`/admin/users/${userId}`),
    getAdminActions: (params = {}) => axios.get('/admin/actions', { params }),
    getStats: () => axios.get('/admin/stats'),
    getSettings: () => axios.get('/admin/settings'),
    updateSettings: (settings) => axios.post('/admin/settings', settings),
    getSystemStatus: () => axios.get('/admin/system-status'),
    getReports: (params) => axios.get('/admin/reports', { params }),
    updateReport: (id, data) => axios.patch(`/admin/reports/${id}`, data),
    getTickets: (params) => axios.get('/admin/support-tickets', { params }),
    updateTicket: (id, data) => axios.patch(`/admin/support-tickets/${id}`, data),
    addTicketNote: (id, note) => axios.post(`/admin/support-tickets/${id}/notes`, { note }),
    replyTicket: (id, message, resolve) => axios.post(`/admin/support-tickets/${id}/reply`, { message, resolve }),
    getStories: (params) => axios.get('/admin/success-stories', { params }),
    reviewStory: (id, data) => axios.patch(`/admin/success-stories/${id}`, data),
    getMembers: (params) => axios.get('/admin/members', { params }),
    getMemberSummary: (id) => axios.get(`/admin/members/${id}/summary`),
    addMemberNote: (id, note) => axios.post(`/admin/members/${id}/notes`, { note }),
    getMemberMessages: (id) => axios.get(`/admin/members/${id}/messages`),
    sendMemberMessage: (id, data) => axios.post(`/admin/members/${id}/messages`, data),
    resendMemberMessageEmail: (id, messageId) => axios.post(`/admin/members/${id}/messages/${messageId}/resend-email`),
    replyMemberMessage: (id, messageId, data) => axios.post(`/admin/members/${id}/messages/${messageId}/replies`, data),
    setMemberThreadStatus: (id, messageId, status) => axios.patch(`/admin/members/${id}/messages/${messageId}`, { status }),
    markMemberThreadSeen: (id, messageId) => axios.post(`/admin/members/${id}/messages/${messageId}/mark-seen`),
    getUnansweredThreads: () => axios.get('/admin/members/inbox/unanswered'),
    getSpamLog: (params) => axios.get('/admin/support/spam-log', { params }),
    changePassword: (data) => axios.post('/admin/change-password', data),
    getMonitoring: (params) => axios.get('/admin/monitoring', { params }),
    getTestAccounts: () => axios.get('/admin/test-accounts'),
    resetTestInteractions: () => axios.post('/admin/test-accounts/reset-interactions')
  },

  safety: {
    block: (userId) => axios.post(`/safety/block/${userId}`),
    unblock: (userId) => axios.delete(`/safety/block/${userId}`),
    listBlocks: () => axios.get('/safety/blocks'),
    report: (data) => axios.post('/safety/report', data)
  },

  support: {
    contact: (data) => axios.post('/support/contact', data),
    reportIssue: (data) => axios.post('/support/issues', data)
  },

  stories: {
    submit: (data) => axios.post('/stories/submit', data),
    mine: () => axios.get('/stories/mine'),
    withdraw: (id) => axios.post(`/stories/${id}/withdraw`)
  },

  inbox: {
    list: () => axios.get('/inbox'),
    get: (id) => axios.get(`/inbox/${id}`),
    reply: (id, body) => axios.post(`/inbox/${id}/replies`, { body })
  },

  account: {
    changePassword: (data) => axios.post('/auth/change-password', data),
    deleteAccount: (data) => axios.delete('/users/me', { data })
  }
};

export const formatApiError = (error, fallback = 'Something went wrong. Please try again.') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return detail?.msg || fallback;
};

// Simplified API for new components  
const api = {
  get: (url, config) => axios.get(url, config),
  post: (url, data, config) => axios.post(url, data, config),
  put: (url, data, config) => axios.put(url, data, config),
  patch: (url, data, config) => axios.patch(url, data, config),
  delete: (url, config) => axios.delete(url, config)
};

export default api;
export { apiService };